import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader, CardFooter } from '../components/ui/Card';
import { StatChip } from '../components/ui/StatChip';
import { CtaSection } from '../components/sections/CtaSection';
import { BookingEmbed } from '../components/sections/BookingEmbed';
import { CheckIcon, AwardIcon, LightbulbIcon, BriefcaseIcon, UsersIcon, GraduationCapIcon, StarIcon, TrendingUpIcon, BarChartIcon, BuildingIcon, CalendarIcon, ClipboardCheckIcon, DollarSignIcon, HeartIcon, UserIcon, HandshakeIcon, TargetIcon, BookOpenIcon, PresentationIcon, BedIcon, UtensilsIcon, MapPinIcon, MusicIcon, GlassesIcon, ShoppingBagIcon, PartyPopperIcon } from 'lucide-react';
export function ElevateImpact() {
  const [activePersona, setActivePersona] = useState('sales');
  const [activeAgendaDay, setActiveAgendaDay] = useState(0);
  const personas = [{
    id: 'sales',
    title: 'Revenue Leaders',
    roles: 'CRO / VP Sales / Sales Enablement',
    icon: <TargetIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1150" />,
    hook: 'Prototype a high-impact SKO in 48 hours—leave with a ready-to-run playbook.',
    value: 'Design an unforgettable SKO/offsite and lift win-rates & quota attainment.'
  }, {
    id: 'executives',
    title: 'C-Suite Support',
    roles: 'Chief of Staff / EA to CEO',
    icon: <BuildingIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1151" />,
    hook: 'Pre-packaged, compliance-ready board/C-suite retreat you can approve in one memo.',
    value: 'Streamline logistics and calendar for board/C-suite offsites with quick approval.'
  }, {
    id: 'strategy',
    title: 'Strategy Leaders',
    roles: 'Strategy / Transformation / PMO',
    icon: <PresentationIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1152" />,
    hook: 'Run a 6-hour strategy sprint with elite faculty + turn into a quarterly cadence.',
    value: 'Create focused strategy sprints away from HQ with facilitated outcomes.'
  }, {
    id: 'partners',
    title: 'Channel & Partners',
    roles: 'Channel / Alliances / Partner Marketing',
    icon: <HandshakeIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1153" />,
    hook: 'Curate a partner summit that upgrades MDF outcomes and pipeline influence.',
    value: 'Host impactful partner councils and tier-1 partner summits.'
  }, {
    id: 'customers',
    title: 'Customer Advocates',
    roles: 'Customer Marketing / Community',
    icon: <UsersIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1154" />,
    hook: 'Blueprint a Customer Advisory Board experience that increases expansion NRR.',
    value: "Create elite customer councils and user group leaders' retreats."
  }, {
    id: 'investors',
    title: 'Investment Leaders',
    roles: 'PE/VC Platform & Portfolio Ops',
    icon: <DollarSignIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1155" />,
    hook: 'Turn your annual portco meetup into a growth lab (templates + faculty included).',
    value: 'Organize portfolio CEO summits and operator bootcamps with measurable outcomes.'
  }, {
    id: 'education',
    title: 'Education Directors',
    roles: 'University Exec-Ed Program Directors',
    icon: <BookOpenIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1156" />,
    hook: 'Co-deliver a custom exec-ed in an immersive setting (CPD-friendly agenda).',
    value: 'Find distinctive venues for custom programs with complete experience packages.'
  }, {
    id: 'hr',
    title: 'People & Culture',
    roles: 'HR Benefits / Wellbeing / DEI',
    icon: <HeartIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1157" />,
    hook: 'Wellbeing x Culture immersion with measurable eNPS lift and CPD-submittable agenda.',
    value: 'Address burnout recovery and culture & belonging initiatives.'
  }, {
    id: 'marketing',
    title: 'Brand Experience',
    roles: 'Corporate Marketing / Field Marketing',
    icon: <StarIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1158" />,
    hook: 'Host a lighthouse client experience that produces case-study content in 72 hours.',
    value: 'Create flagship clienteling or product-launch councils with measurable outcomes.'
  }];
  const valuePropositions = {
    sales: {
      title: 'Revenue Leaders',
      subtitle: 'Sales, Alliances, Customer & Field Marketing',
      points: ['Ready-to-run SKO/partner/CAB agenda, demonstrated live', 'Measurable pipeline/NRR impact model with metrics dashboard', 'Co-created content templates you can immediately implement', 'Peer networking with other revenue leaders across industries'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eWtMdvKbNH1JRdSABJZYQq/revenue_leaders.webp'
    },
    executives: {
      title: 'C-Suite Support',
      subtitle: 'Chiefs of Staff, Executive Assistants, Board Coordinators',
      points: ["'One-memo approval' package with complete agenda, budget, and ROI sheet", 'Risk/compliance documentation ready for legal review', 'Turnkey logistics that reduce your planning overhead by 80%', 'Executive-ready presentations to secure immediate buy-in'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gNWosE5Jbo5bFaRab3kErc/C-Suite_Support.webp'
    },
    strategy: {
      title: 'Strategy Leaders',
      subtitle: 'Strategy, Transformation & PMO Directors',
      points: ['Facilitated strategy sprint framework with proven outcomes', 'Quarterly cadence template to maintain momentum post-event', 'Cross-functional alignment tools designed by top consultants', 'Implementation roadmap with accountable next steps'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ipW6Tej1KGgnHm3RZoXnjq/Strategy_Leaders.webp'
    },
    partners: {
      title: 'Channel & Partners',
      subtitle: 'Alliances, Channel & Partner Marketing Leaders',
      points: ['Partner summit blueprint that maximizes MDF utilization', 'Pipeline influence measurement framework', 'Tier-based partner experience design with scalable components', 'Co-marketing and enablement templates ready for implementation'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/xnn63jppx6boMThv6udZ2i/Channel__Partners.webp'
    },
    customers: {
      title: 'Customer Advocates',
      subtitle: 'Customer Marketing, Community & Developer Relations',
      points: ['Customer Advisory Board framework that drives expansion revenue', 'Community leader engagement model with retention metrics', 'Voice of Customer capture methodology with product feedback loop', 'Case study and testimonial generation process built-in'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cVKntsF6xzcD7RLw6fhj5g/customer_advocates.webp'
    },
    investors: {
      title: 'Investment Leaders',
      subtitle: 'PE/VC Platform & Portfolio Operations',
      points: ['Portfolio CEO summit design with value-creation workshops', 'Cross-portfolio playbook exchange framework', 'Filmed case content for LP communications', 'Operator bootcamp template with measurable acceleration metrics'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iuCbZHofR8exRRSctpdxiv/Investment_Leaders.webp'
    },
    education: {
      title: 'Education Directors',
      subtitle: 'University Exec-Ed & Custom Program Directors',
      points: ['White-label executive education environment in an iconic setting', 'CPD-compliant agenda structure ready for accreditation', 'Sponsor-ready program components with monetization options', 'Faculty support and content integration with your curriculum'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tkXanW38ZiLxzQB2DMayJJ/Education_Directors.webp'
    },
    hr: {
      title: 'People & Culture',
      subtitle: 'HR, Wellbeing & DEI Leaders',
      points: ['CPD-submittable learning hours for professional development', 'Measurable wellbeing outcomes with eNPS impact tracking', 'Culture rituals designed for continuation back at the workplace', 'Belonging and inclusion frameworks for diverse teams'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8uiDfdd9ntoVUrd6vwC6mh/people_and_culture.webp'
    },
    marketing: {
      title: 'Brand Experience',
      subtitle: 'Corporate Marketing, Brand & Field Marketing',
      points: ['Lighthouse client experience blueprint with measurable outcomes', 'Case study content production integrated into the experience', 'Product launch council framework with influencer components', 'Experiential marketing measurement with attribution modeling'],
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/76ECJnoHVeJhcDMLtK7UTH/Brand_Experience.webp'
    }
  };
  const agendaItems = [{
    day: 'Day 1',
    timeframe: 'Afternoon & Evening',
    items: [{
      time: '2:00 PM',
      title: 'VIP Arrivals & Welcome',
      description: 'Private check-in experience with personalized welcome'
    }, {
      time: '3:30 PM',
      title: 'Destination ROI Masterclass',
      description: '90-minute session on maximizing event impact and measuring outcomes'
    }, {
      time: '6:00 PM',
      title: 'Immersive Welcome Reception',
      description: 'Networking and showcase of destination experiences'
    }, {
      time: '7:30 PM',
      title: 'Curated Dining Experience',
      description: 'Signature culinary journey with facilitated connections'
    }, {
      time: '9:00 PM',
      title: 'Live Concert Experience',
      description: "Enjoy Hard Rock's signature live music entertainment"
    }]
  }, {
    day: 'Day 2',
    timeframe: 'Full Day Experience',
    items: [{
      time: '7:00 AM',
      title: 'Optional Wellness Activity',
      description: 'Sunrise yoga or guided beach meditation'
    }, {
      time: '8:30 AM',
      title: 'Breakfast & Track Selection',
      description: 'Choose your focused learning track'
    }, {
      time: '9:30 AM',
      title: 'Specialized Tracks',
      description: 'Revenue SKO / People & Culture / Partner Council (3 hours)'
    }, {
      time: '12:30 PM',
      title: 'Collaborative Lunch',
      description: 'Shared insights and cross-track pollination'
    }, {
      time: '2:00 PM',
      title: 'Site Tours & Experiential Showcase',
      description: 'Curated exploration of venue capabilities'
    }, {
      time: '4:00 PM',
      title: 'Design Lab',
      description: 'Facilitated workshop to apply learnings to your specific needs'
    }, {
      time: '7:00 PM',
      title: 'Signature Networking Experience',
      description: 'Immersive evening with local cultural elements'
    }, {
      time: '9:30 PM',
      title: 'Themed Party Night',
      description: "Hard Rock's famous themed entertainment event"
    }]
  }, {
    day: 'Day 3',
    timeframe: 'Morning',
    items: [{
      time: '8:00 AM',
      title: 'Breakfast & Reflection',
      description: 'Guided discussion on key takeaways'
    }, {
      time: '9:00 AM',
      title: 'Playbook Finalization',
      description: 'Complete your actionable framework with expert guidance'
    }, {
      time: '10:30 AM',
      title: 'One-to-One Consultations',
      description: 'Personalized next steps with our specialists'
    }, {
      time: '12:00 PM',
      title: 'Farewell Lunch & Departures',
      description: 'Closing remarks and continuation planning'
    }]
  }];
  const hardRockActivities = [{
    title: 'Live Music & Concerts',
    icon: <MusicIcon className="h-8 w-8 text-white" data-id="element-1159" />,
    description: 'Enjoy nightly concerts featuring top tribute bands and local talent on multiple stages throughout the resort.'
  }, {
    title: 'Legendary Pool Parties',
    icon: <PartyPopperIcon className="h-8 w-8 text-white" data-id="element-1160" />,
    description: 'Experience the famous Hard Rock pool parties with DJs, foam parties, and poolside entertainment.'
  }, {
    title: 'Themed Nights & Shows',
    icon: <PartyPopperIcon className="h-8 w-8 text-white" data-id="element-1161" />,
    description: 'Participate in themed dinner shows, Mexican fiestas, and professional entertainment productions.'
  }, {
    title: 'Rock Spa® Treatments',
    icon: <HeartIcon className="h-8 w-8 text-white" data-id="element-1162" />,
    description: 'Unwind with signature rhythm and motion treatments, massages, and wellness therapies.'
  }, {
    title: 'Music Amenities Program',
    icon: <GlassesIcon className="h-8 w-8 text-white" data-id="element-1163" />,
    description: 'Borrow a Fender® guitar, mixing board, or vinyl record player delivered to your room.'
  }, {
    title: 'VIP Access Benefits',
    icon: <ShoppingBagIcon className="h-8 w-8 text-white" data-id="element-1164" />,
    description: 'Enjoy exclusive access to premium amenities, priority reservations, and personalized concierge service.'
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1165">
      {/* Hero */}
      <section className="relative py-32 md:py-40 overflow-hidden min-h-[600px]" data-id="element-1166">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-1167">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6bivUiY37qndHSDGqhUPNF/Complimentary_Retreat_Hero_Page.webp" alt="Escape to Paradise - Cancun Beach" className="w-full h-full object-cover scale-105" data-id="element-1168" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" data-id="element-1169"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1170">
          <div className="max-w-3xl" data-id="element-1171">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 drop-shadow-2xl" data-id="element-1172">
              Your Complimentary Luxury Retreat
            </h1>
            <p className="text-xl md:text-2xl text-white mb-8 drop-shadow-2xl leading-relaxed" data-id="element-1173">
              Escape to paradise for a FREE two-night strategic immersion where
              elite decision-makers design unforgettable corporate experiences
              that drive measurable results
            </p>
            <div className="mt-8" data-id="element-1174">
              <Button variant="primary" size="lg" to="/contact" data-id="element-1175">
                Apply for Invitation
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Persona Selector - Redesigned for better mobile experience */}
      <section className="py-12 bg-white" data-id="element-1176">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1177">
          <div className="max-w-3xl mx-auto text-center mb-8" data-id="element-1178">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1179">
              Tailored for Decision-Makers Like You
            </h2>
            <p className="text-lg text-[#374047]/80 mb-4" data-id="element-1180">
              Select your role below to see content specific to your objectives
            </p>
            {/* Visual indicator to show this is interactive */}
            <div className="inline-flex items-center bg-[#0099A1]/10 px-4 py-2 rounded-lg text-[#0099A1] font-medium mb-6" data-id="element-1181">
              <CalendarIcon className="h-5 w-5 mr-2" data-id="element-1182" />
              <span data-id="element-1183">Tap a role to view tailored content</span>
            </div>
          </div>
          {/* Mobile-optimized role selector */}
          <div className="overflow-x-auto pb-4 mb-6" data-id="element-1184">
            <div className="flex space-x-2 min-w-max px-1" data-id="element-1185">
              {personas.map(persona => <button key={persona.id} onClick={() => setActivePersona(persona.id)} className={`flex flex-col items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors ${activePersona === persona.id ? 'bg-[#0099A1] text-white' : 'bg-[#F4E8D0]/50 text-[#374047] hover:bg-[#0099A1]/10'}`} data-id="element-1186">
                  <span className="p-2 bg-white/80 rounded-full mb-2" data-id="element-1187">
                    {persona.icon}
                  </span>
                  <span className="text-center whitespace-nowrap" data-id="element-1188">
                    {persona.title}
                  </span>
                  {activePersona === persona.id && <div className="h-1 w-12 bg-[#FF6B65] rounded-full mt-2" data-id="element-1189"></div>}
                </button>)}
            </div>
          </div>
          {/* Active persona indicator */}
          <div className="bg-[#F4E8D0]/30 rounded-lg p-4 mb-6" data-id="element-1190">
            <div className="flex items-center justify-between" data-id="element-1191">
              <div className="flex items-center" data-id="element-1192">
                <div className="bg-[#0099A1] p-2 rounded-full text-white mr-3" data-id="element-1193">
                  {personas.find(p => p.id === activePersona)?.icon}
                </div>
                <div data-id="element-1194">
                  <h3 className="font-bold text-[#374047]" data-id="element-1195">
                    {valuePropositions[activePersona].title}
                  </h3>
                  <p className="text-sm text-[#374047]/70" data-id="element-1196">
                    {personas.find(p => p.id === activePersona)?.roles}
                  </p>
                </div>
              </div>
              <div className="bg-[#0099A1]/10 px-3 py-1 rounded-full text-xs text-[#0099A1] font-medium" data-id="element-1197">
                Selected
              </div>
            </div>
          </div>
          {/* Active Persona Value Proposition - Redesigned without image */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-8" data-id="element-1198">
            {/* Hero Image for Active Persona */}
            <div className="relative h-64 md:h-80 overflow-hidden" data-id="element-1199">
              <img src={valuePropositions[activePersona].image} alt={valuePropositions[activePersona].title} className="w-full h-full object-cover object-center" data-id="element-1200" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" data-id="element-1201"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-1202">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 drop-shadow-lg" data-id="element-1203">
                  {valuePropositions[activePersona].title}
                </h3>
                <p className="text-white/95 text-sm md:text-base drop-shadow-lg" data-id="element-1204">
                  {valuePropositions[activePersona].subtitle}
                </p>
              </div>
            </div>

            <div className="p-6" data-id="element-1205">
              <div className="border-l-4 border-[#FF6B65] pl-4 mb-6" data-id="element-1206">
                <p className="text-lg italic text-[#374047]" data-id="element-1207">
                  "{personas.find(p => p.id === activePersona)?.hook}"
                </p>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1208">
                {personas.find(p => p.id === activePersona)?.value}
              </p>
              <h4 className="font-bold text-[#374047] mb-4" data-id="element-1209">Key Benefits:</h4>
              <div className="space-y-4 mb-8" data-id="element-1210">
                {valuePropositions[activePersona].points.map((point, index) => <div key={index} className="flex items-start bg-[#F4E8D0]/20 p-3 rounded-lg" data-id="element-1211">
                    <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1212" />
                    <span className="ml-3 text-[#374047]" data-id="element-1213">{point}</span>
                  </div>)}
              </div>
              <div className="flex justify-center" data-id="element-1214">
                <Button variant="primary" to="/contact" data-id="element-1215">
                  Request Your Invitation
                </Button>
              </div>
            </div>
          </div>
          {/* Navigation hints */}
          <div className="text-center text-[#374047]/60 text-sm mb-8" data-id="element-1216">
            <p data-id="element-1217">
              Scroll through the roles above to find the best fit for your
              position
            </p>
          </div>
        </div>
      </section>
      {/* Package Details - Condensed version */}
      <section className="py-12 bg-[#F4E8D0]/30" data-id="element-1218">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1219">
          <div className="max-w-3xl mx-auto text-center mb-8" data-id="element-1220">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1221">
              Your Exclusive 2-Night Invitation
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1222">
              Limited to 20 qualified decision-makers per cohort
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1223">
            <div data-id="element-1224">
              <h3 className="text-xl font-bold text-[#374047] mb-4 flex items-center" data-id="element-1225">
                <CheckIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1226" />
                What's Included
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-id="element-1227">
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1228">
                  <BedIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1229" />
                  <span className="text-sm" data-id="element-1230">
                    Two nights at a luxury property
                  </span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1231">
                  <UtensilsIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1232" />
                  <span className="text-sm" data-id="element-1233">Curated culinary experiences</span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1234">
                  <MapPinIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1235" />
                  <span className="text-sm" data-id="element-1236">Local transfers and logistics</span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1237">
                  <BookOpenIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1238" />
                  <span className="text-sm" data-id="element-1239">Workshops with elite faculty</span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1240">
                  <ClipboardCheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1241" />
                  <span className="text-sm" data-id="element-1242">Complete work outputs</span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1243">
                  <HeartIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1244" />
                  <span className="text-sm" data-id="element-1245">Personal wellness elements</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#374047] mb-4 flex items-center" data-id="element-1246">
                <DollarSignIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1247" />
                What You Cover
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1248">
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1249">
                  <CalendarIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1250" />
                  <span className="text-sm" data-id="element-1251">
                    Your flights to the destination
                  </span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1252">
                  <DollarSignIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1253" />
                  <span className="text-sm" data-id="element-1254">$250 refundable deposit</span>
                </div>
              </div>
              <div className="mt-6" data-id="element-1255">
                <Button variant="primary" to="/contact" data-id="element-1256">
                  Check Eligibility
                </Button>
              </div>
            </div>
            <div data-id="element-1257">
              <Card className="h-full" data-id="element-1258">
                <CardHeader className="bg-[#0099A1] text-white p-4" data-id="element-1259">
                  <h3 className="text-xl font-bold" data-id="element-1260">
                    Compliance-Ready Package
                  </h3>
                </CardHeader>
                <CardBody className="p-6" data-id="element-1261">
                  <div className="grid grid-cols-2 gap-4" data-id="element-1262">
                    <div className="bg-white/60 rounded-lg p-3" data-id="element-1263">
                      <div className="flex items-center mb-2" data-id="element-1264">
                        <ClipboardCheckIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1265" />
                        <h4 className="font-medium text-[#374047]" data-id="element-1266">
                          Official Letter
                        </h4>
                      </div>
                      <p className="text-xs text-[#374047]/70" data-id="element-1267">
                        Educational objectives & hospitality breakdown for
                        approval
                      </p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3" data-id="element-1268">
                      <div className="flex items-center mb-2" data-id="element-1269">
                        <BookOpenIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1270" />
                        <h4 className="font-medium text-[#374047]" data-id="element-1271">
                          CPD Agenda
                        </h4>
                      </div>
                      <p className="text-xs text-[#374047]/70" data-id="element-1272">
                        Submit for professional development credit
                      </p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3" data-id="element-1273">
                      <div className="flex items-center mb-2" data-id="element-1274">
                        <DollarSignIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1275" />
                        <h4 className="font-medium text-[#374047]" data-id="element-1276">
                          Value Disclosure
                        </h4>
                      </div>
                      <p className="text-xs text-[#374047]/70" data-id="element-1277">
                        Transparent line-item benefits for compliance
                      </p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3" data-id="element-1278">
                      <div className="flex items-center mb-2" data-id="element-1279">
                        <UserIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1280" />
                        <h4 className="font-medium text-[#374047]" data-id="element-1281">
                          No Obligation
                        </h4>
                      </div>
                      <p className="text-xs text-[#374047]/70" data-id="element-1282">
                        Clear documentation of no-pressure policy
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </section>
      {/* Sample Agenda - Converted to tabs */}
      <section className="py-16 bg-white" data-id="element-1283">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1284">
          <div className="max-w-3xl mx-auto text-center mb-8" data-id="element-1285">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1286">
              Sample 48-Hour Agenda
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1287">
              A carefully crafted experience with modular tracks to address your
              specific needs
            </p>
          </div>
          {/* Day Tabs */}
          <div className="flex border-b border-gray-200 mb-6 overflow-x-auto" data-id="element-1288">
            {agendaItems.map((day, index) => <button key={index} onClick={() => setActiveAgendaDay(index)} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${activeAgendaDay === index ? 'border-b-2 border-[#0099A1] text-[#0099A1]' : 'text-[#374047]/70 hover:text-[#374047]'}`} data-id="element-1289">
                <div className="flex items-center" data-id="element-1290">
                  <div className="w-6 h-6 rounded-full bg-[#0099A1]/10 text-[#0099A1] flex items-center justify-center mr-2 text-xs" data-id="element-1291">
                    {index + 1}
                  </div>
                  {day.day} - {day.timeframe}
                </div>
              </button>)}
          </div>
          {/* Active Day Content */}
          <div className="bg-[#F4E8D0]/20 rounded-xl p-6" data-id="element-1292">
            <div className="space-y-6" data-id="element-1293">
              {agendaItems[activeAgendaDay].items.map((item, itemIndex) => <div key={itemIndex} className="flex" data-id="element-1294">
                  <div className="w-24 flex-shrink-0 text-[#374047]/70 font-medium" data-id="element-1295">
                    {item.time}
                  </div>
                  <div data-id="element-1296">
                    <h4 className="font-medium text-[#374047]" data-id="element-1297">{item.title}</h4>
                    <p className="text-sm text-[#374047]/70" data-id="element-1298">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
          <div className="max-w-3xl mx-auto text-center mt-8" data-id="element-1299">
            <p className="text-[#374047]/70" data-id="element-1300">
              This agenda is customized for each cohort based on the specific
              roles and objectives of participants
            </p>
          </div>
        </div>
      </section>
      {/* Luxury Accommodations */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1301">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1302">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1303">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1304">
              Your Luxury Accommodations
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1305">
              Enjoy a complimentary two-night stay at the iconic Hard Rock Hotel
              Cancun
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-1306">
            <div data-id="element-1307">
              <div className="flex items-center mb-6" data-id="element-1308">
                <BedIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-1309" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1310">
                  Luxurious Oceanfront Experience
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1311">
                Experience the ultimate comfort in meticulously designed rooms
                featuring ocean views, hydro spa tubs, and premium amenities.
                Your complimentary stay includes:
              </p>
              <div className="space-y-4 mb-8" data-id="element-1312">
                <div className="flex items-start" data-id="element-1313">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1314" />
                  <span className="ml-3 text-[#374047]" data-id="element-1315">
                    Spacious suites with breathtaking Caribbean views
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1316">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1317" />
                  <span className="ml-3 text-[#374047]" data-id="element-1318">
                    In-room hydro spa tubs for ultimate relaxation
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1319">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1320" />
                  <span className="ml-3 text-[#374047]" data-id="element-1321">
                    Private balconies with panoramic views
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1322">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1323" />
                  <span className="ml-3 text-[#374047]" data-id="element-1324">
                    Premium amenities and 24-hour room service
                  </span>
                </div>
              </div>
              <div className="flex items-center mb-6" data-id="element-1325">
                <UtensilsIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-1326" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1327">
                  World-Class Dining
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1328">
                Indulge in exceptional culinary experiences across multiple
                restaurants offering international cuisines prepared by skilled
                chefs, including a hosted dinner at the acclaimed Bombay-Cancun
                restaurant.
              </p>
              <div className="flex items-center mb-6" data-id="element-1329">
                <MapPinIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-1330" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1331">
                  Perfect Location
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1332">
                Situated in the heart of Cancun's Hotel Zone, you'll enjoy
                pristine beaches, infinity pools, and easy access to local
                attractions while focusing on your strategic planning.
              </p>
              <Button variant="primary" to="/contact" data-id="element-1333">
                Apply for Your Stay
              </Button>
            </div>
            <div data-id="element-1334">
              <div className="grid grid-cols-2 gap-4" data-id="element-1335">
                <div className="overflow-hidden rounded-lg" data-id="element-1336">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uqWCKaxJJQHPR63bhKsnNi/hard-rock-hotel-cancun-rock-suite-ocean-view-king-bed.jpg" alt="Hard Rock Hotel Cancun Suite" className="w-full h-48 object-cover" data-id="element-1337" />
                </div>
                <div className="overflow-hidden rounded-lg" data-id="element-1338">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/32xHwSugA785R6rs27jzyA/13830703_ImageLargeWidth.jpg" alt="Hard Rock Hotel Cancun Pool" className="w-full h-48 object-cover" data-id="element-1339" />
                </div>
                <div className="overflow-hidden rounded-lg" data-id="element-1340">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4zXYgDX4yC8uvXGZ5Lt7Q5/fine-dining-all-inclusive-hard-rock-cancun.jpg" alt="Hard Rock Hotel Cancun Dining" className="w-full h-48 object-cover" data-id="element-1341" />
                </div>
                <div className="overflow-hidden rounded-lg" data-id="element-1342">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vjnr4pnk2Td97NQSPj6RK5/HRH_Cancun_Pool_Beach_013_6465.jpg" alt="Hard Rock Hotel Cancun Beach" className="w-full h-48 object-cover" data-id="element-1343" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Hard Rock Activities */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1344">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1345">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1346">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1347">
              Hard Rock Cancun Entertainment & Activities
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1348">
              Work hard, play harder with these amazing experiences included in
              your stay
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1349">
            {hardRockActivities.map((activity, index) => <div key={index} className="bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-xl p-6 text-white shadow-lg transform transition-transform hover:scale-105" data-id="element-1350">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4" data-id="element-1351">
                  {activity.icon}
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1352">{activity.title}</h3>
                <p className="text-white/80" data-id="element-1353">{activity.description}</p>
              </div>)}
          </div>
          <div className="max-w-3xl mx-auto text-center mt-12" data-id="element-1354">
            <p className="text-[#374047]/70 mb-6" data-id="element-1355">
              All activities and entertainment are included in your
              complimentary stay
            </p>
            <Button variant="secondary" to="/contact" data-id="element-1356">
              Apply for Your Rock Star Experience
            </Button>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 relative overflow-hidden" style={{
      background: 'linear-gradient(to right, #0099A1, #006D73)'
    }} data-id="element-1357">
        <div className="absolute inset-0 opacity-20" data-id="element-1358">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vjnr4pnk2Td97NQSPj6RK5/HRH_Cancun_Pool_Beach_013_6465.jpg" alt="Hard Rock Hotel Cancun Background" className="w-full h-full object-cover" data-id="element-1359" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1360">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1361">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/d4ZJyP7bz15Ef5qLdnhr7b/HardRockHotelLogo-White3x.png" alt="Hard Rock Hotel Logo" className="h-16 mx-auto mb-6" data-id="element-1362" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1363">
              Limited Availability
            </h2>
            <p className="text-xl text-white/90 mb-8" data-id="element-1364">
              Only 20 qualified decision-makers per cohort. Secure your spot
              today.
            </p>
            <Button variant="primary" size="lg" to="/contact" data-id="element-1365">
              Apply for Your Invitation
            </Button>
            <p className="text-sm text-white/70 mt-4" data-id="element-1366">
              Our team will review your application within 48 hours
            </p>
          </div>
        </div>
      </section>
      {/* Google Booking Embed */}
      <BookingEmbed data-id="element-1367" />
      <CtaSection data-id="element-1368" />
    </div>;
}